
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Mail, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface Referent {
  id: string;
  name: string;
  email: string;
  status: string;
  relation_type: string;
  last_email_sent_at: string | null;
  reminders_sent_count: number;
}

interface ReferentSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: {
    id: string;
    candidate_name: string;
    candidate_email?: string;
    status?: string;
    referents?: Referent[];
  } | null;
  onSuccess: () => void;
}

export const ReferentSelectionModal = ({
  open,
  onOpenChange,
  campaign,
  onSuccess
}: ReferentSelectionModalProps) => {
  const [selectedReferents, setSelectedReferents] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [candidateRemindersCount, setCandidateRemindersCount] = useState<number>(0);
  const { toast } = useToast();

  // Filtrer les référents non complétés
  const eligibleReferents = campaign?.referents?.filter(r => r.status !== 'Complété') || [];

  // Charger le nombre de relances candidat
  useEffect(() => {
    const loadCandidateReminders = async () => {
      if (!campaign?.id) return;

      const { data, error } = await supabase
        .from('message_logs')
        .select('id')
        .eq('campaign_id', campaign.id)
        .in('email_type', ['candidate_reminder', 'referent_submission_reminder', 'referent_reminder'])
        .eq('status', 'sent');

      if (!error && data) {
        setCandidateRemindersCount(data.length);
      }
    };

    if (open && campaign?.id) {
      loadCandidateReminders();
    }
  }, [open, campaign?.id]);

  const handleSelectAll = () => {
    if (selectedReferents.length === eligibleReferents.length) {
      setSelectedReferents([]);
    } else {
      setSelectedReferents(eligibleReferents.map(r => r.id));
    }
  };

  const handleReferentToggle = (referentId: string) => {
    setSelectedReferents(prev => 
      prev.includes(referentId)
        ? prev.filter(id => id !== referentId)
        : [...prev, referentId]
    );
  };

  const handleCandidateReminder = async () => {
    if (!campaign) return;
    
    setIsLoading(true);
    try {
      console.log("Relance candidat pour soumission de référents");
      const { error } = await supabase.functions.invoke('send-candidate-reminder', {
        body: { 
          campaign_id: campaign.id,
          type: 'referent_submission_reminder'
        },
      });

      if (error) {
        console.error("Erreur relance candidat:", error);
        throw new Error(error.message || "Erreur lors de l'envoi de la relance candidat");
      }

      toast({
        title: "Relance envoyée au candidat",
        description: `Un rappel a été envoyé à ${campaign.candidate_name} pour soumettre sa liste de référents.`,
      });
      
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Erreur envoi relance candidat:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'envoyer la relance candidat",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendReminders = async () => {
    if (!campaign) return;
    
    if (selectedReferents.length === 0) {
      toast({
        title: "Aucune sélection",
        description: "Veuillez sélectionner au moins un référent.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      console.log('🚀 Envoi relances multiples:', { 
        campaignId: campaign.id, 
        selectedReferents 
      });

      // Utiliser send-referent-emails avec les référents sélectionnés
      const { data, error } = await supabase.functions.invoke('send-referent-emails', {
        body: { 
          campaign_id: campaign.id,
          selected_referents: selectedReferents
        },
      });

      if (error) {
        console.error("❌ Erreur relance référents:", error);
        
        // Améliorer les messages d'erreur
        let errorMessage = "Erreur lors de l'envoi des relances";
        
        if (error.message?.includes('network') || error.message?.includes('fetch')) {
          errorMessage = "Problème de connexion. Vérifiez votre internet et réessayez.";
        } else if (error.message?.includes('authorization')) {
          errorMessage = "Problème d'autorisation. Reconnectez-vous et réessayez.";
        } else if (error.message?.includes('CORS')) {
          errorMessage = "Problème technique temporaire. Réessayez dans quelques instants.";
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        throw new Error(errorMessage);
      }

      console.log('✅ Réponse edge function:', data);
      
      const emailsSent = data?.emails_sent || 0;
      const emailsFailed = data?.emails_failed || 0;

      if (emailsSent > 0) {
        toast({
          title: "Relances envoyées",
          description: `${emailsSent} relance(s) envoyée(s) avec succès${emailsFailed > 0 ? `. ${emailsFailed} échec(s)` : ''}.`,
        });
      } else {
        toast({
          title: "Aucun email envoyé",
          description: "Les emails ont peut-être déjà été envoyés récemment.",
        });
      }

      onSuccess();
      onOpenChange(false);
      setSelectedReferents([]); // Reset selection
    } catch (error: any) {
      console.error('💥 Erreur envoi relances:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'envoyer les relances",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };



  // Ne pas rendre le composant si campaign est null
  if (!campaign) {
    return null;
  }

  // Déterminer si c'est "En attente candidat"
  const isPendingCandidate = campaign.status === 'En attente candidat';
  
  console.log('🔍 ReferentSelectionModal - Campaign status:', campaign.status, 'isPendingCandidate:', isPendingCandidate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="p-0 border-0 bg-transparent shadow-none max-w-none w-auto"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          const dialogElement = e.currentTarget as HTMLElement;
          const firstButton = dialogElement.querySelector('button');
          if (firstButton) {
            setTimeout(() => firstButton.focus(), 100);
          }
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">
          {isPendingCandidate ? 'Relancer le candidat' : 'Sélectionnez les référents à relancer'}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Campagne {campaign.candidate_name} - {isPendingCandidate ? 'En attente candidat' : `${eligibleReferents.length} références éligibles`}
        </DialogDescription>
        <div className="w-[560px] p-6 bg-white rounded-[32px] inline-flex flex-col justify-start items-start gap-6">
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="justify-start text-gray-800 text-2xl font-medium font-['Bricolage_Grotesque']">
              {isPendingCandidate ? 'Relancer le candidat' : 'Sélectionnez les référents à relancer'}
            </div>
            <div className="justify-start">
              <span className="text-neutral-400 text-base font-normal font-['Bricolage_Grotesque']">Campagne:</span>
              <span className="text-gray-800 text-base font-normal font-['Bricolage_Grotesque']"> {campaign.candidate_name} • {isPendingCandidate ? 'En attente candidat' : `${eligibleReferents.length} références éligibles`}</span>
            </div>
          </div>

          {isPendingCandidate ? (
            <div className="self-stretch flex flex-col justify-start items-start gap-3">
              <div className="self-stretch inline-flex justify-between items-center">
                <div className="justify-start">
                  <span className="text-gray-800 text-base font-semibold font-['Bricolage_Grotesque']">Candidat</span>
                  <span className="text-gray-800 text-base font-bold font-['Bricolage_Grotesque']"> </span>
                  <span className="text-gray-800 text-xs font-medium font-['Bricolage_Grotesque']">(1 candidat à relancer)</span>
                </div>
              </div>

              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                <div className="self-stretch p-3 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex flex-col justify-start items-start gap-3">
                  <div className="self-stretch inline-flex justify-start items-center gap-3">
                    <div className="size-4 relative bg-teal-400 rounded-[4.80px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 overflow-hidden transition-colors">
                      <div className="left-[2.40px] top-[2.65px] absolute">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2.3252 6.27949L4.7752 8.72949L9.6752 3.47949" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                    <div className="w-96 inline-flex flex-col justify-start items-start gap-1">
                      <div className="self-stretch justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
                        {campaign.candidate_name} (Candidat)
                      </div>
                      <div className="inline-flex justify-start items-center gap-2">
                        <div className="justify-start text-gray-800 text-xs font-normal font-['Bricolage_Grotesque']">
                          {campaign.candidate_email || 'Email non disponible'}
                        </div>
                        <div className="w-2.5 h-0 origin-top-left rotate-90 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>
                        <div className="justify-start text-gray-800 text-xs font-normal font-['Bricolage_Grotesque']">
                          En attente de soumission
                        </div>
                        <div className="w-2.5 h-0 origin-top-left rotate-90 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>
                        <div className="justify-start text-gray-800 text-xs font-normal font-['Bricolage_Grotesque']">
                          {candidateRemindersCount} Relance(s)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="self-stretch inline-flex justify-end items-center gap-3">
                <button
                  onClick={() => onOpenChange(false)}
                  disabled={isLoading}
                  className="h-10 p-3 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-start items-center gap-1 overflow-hidden hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <div className="justify-center text-neutral-400 text-sm font-semibold font-['Bricolage_Grotesque']">
                    Annuler
                  </div>
                </button>
                <button
                  onClick={handleCandidateReminder}
                  disabled={isLoading}
                  className="h-10 p-3 bg-teal-400 rounded-[10px] flex justify-start items-center gap-1 overflow-hidden hover:bg-teal-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="justify-center text-white text-sm font-semibold font-['Bricolage_Grotesque']">
                    {isLoading ? 'Envoi en cours...' : 'Relancer'}
                  </div>
                </button>
              </div>
            </div>
          ) : !campaign.referents || campaign.referents.length === 0 ? (
            <div className="self-stretch flex flex-col justify-center items-center gap-4 py-8">
              <Mail className="h-12 w-12 text-blue-500" />
              <div className="text-center">
                <p className="text-gray-800 text-lg font-medium font-['Bricolage_Grotesque'] mb-2">Aucun référent soumis</p>
                <p className="text-neutral-400 text-base font-normal font-['Bricolage_Grotesque'] mb-6">Le candidat n'a pas encore soumis sa liste de référents.</p>
              </div>
              <div className="self-stretch inline-flex justify-end items-center gap-3">
                <button
                  onClick={() => onOpenChange(false)}
                  disabled={isLoading}
                  className="h-10 p-3 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-start items-center gap-1 overflow-hidden hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <div className="justify-center text-neutral-400 text-sm font-semibold font-['Bricolage_Grotesque']">
                    Annuler
                  </div>
                </button>
                <button
                  onClick={handleCandidateReminder}
                  disabled={isLoading}
                  className="h-10 p-3 bg-teal-400 rounded-[10px] flex justify-start items-center gap-1 overflow-hidden hover:bg-teal-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="justify-center text-white text-sm font-semibold font-['Bricolage_Grotesque']">
                    {isLoading ? 'Envoi en cours...' : 'Relancer le candidat'}
                  </div>
                </button>
              </div>
            </div>
          ) : eligibleReferents.length === 0 ? (
            <div className="self-stretch flex flex-col justify-center items-center gap-4 py-8">
              <CheckCircle className="h-12 w-12 text-green-500" />
              <div className="text-center">
                <p className="text-gray-800 text-lg font-medium font-['Bricolage_Grotesque'] mb-2">Tous les référents ont complété leur évaluation</p>
                <p className="text-neutral-400 text-base font-normal font-['Bricolage_Grotesque']">Aucune relance nécessaire pour cette campagne.</p>
              </div>
              <div className="self-stretch inline-flex justify-end items-center gap-3">
                <button
                  onClick={() => onOpenChange(false)}
                  className="h-10 p-3 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-start items-center gap-1 overflow-hidden hover:bg-gray-50 transition-colors"
                >
                  <div className="justify-center text-neutral-400 text-sm font-semibold font-['Bricolage_Grotesque']">
                    Fermer
                  </div>
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="self-stretch flex flex-col justify-start items-start gap-3">
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="justify-start">
                    <span className="text-gray-800 text-base font-semibold font-['Bricolage_Grotesque']">Références</span>
                    <span className="text-gray-800 text-base font-bold font-['Bricolage_Grotesque']"> </span>
                    <span className="text-gray-800 text-xs font-medium font-['Bricolage_Grotesque']">({selectedReferents.length} sur {eligibleReferents.length} sélectionnés)</span>
                  </div>
                  <div className="flex justify-start items-center gap-2">
                    <button
                      onClick={handleSelectAll}
                      className="px-1.5 py-[5px] rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-center items-center gap-1 hover:bg-gray-50 transition-colors"
                    >
                      <div className="justify-start text-gray-800 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
                        Tout sélectionner
                      </div>
                    </button>
                    <button
                      onClick={() => setSelectedReferents([])}
                      className={`px-1.5 py-[5px] rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-center items-center gap-1 transition-colors ${
                        selectedReferents.length === 0 ? 'opacity-50' : 'hover:bg-gray-50'
                      }`}
                      disabled={selectedReferents.length === 0}
                    >
                      <div className="justify-start text-gray-800 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
                        Clair
                      </div>
                    </button>
                  </div>
                </div>

                <div className="self-stretch flex flex-col justify-start items-start gap-2">
                  {eligibleReferents.map((referent) => (
                    <div
                      key={`referent-${referent.id}`}
                      className="self-stretch p-3 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex flex-col justify-start items-start gap-3 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleReferentToggle(referent.id)}
                    >
                      <div className="self-stretch inline-flex justify-start items-center gap-3">
                        <div className={`size-4 relative rounded-[4.80px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 overflow-hidden transition-colors ${
                          selectedReferents.includes(referent.id) ? 'bg-teal-400' : 'bg-white'
                        }`}>
                          {selectedReferents.includes(referent.id) && (
                            <div className="left-[2.40px] top-[2.65px] absolute">
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.3252 6.27949L4.7752 8.72949L9.6752 3.47949" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="w-96 inline-flex flex-col justify-start items-start gap-1">
                          <div className="self-stretch justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
                            {referent.name} ({referent.status})
                          </div>
                          <div className="inline-flex justify-start items-center gap-2">
                            <div className="justify-start text-gray-800 text-xs font-normal font-['Bricolage_Grotesque']">
                              {referent.email}
                            </div>
                            <div className="w-2.5 h-0 origin-top-left rotate-90 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>
                            <div className="justify-start text-gray-800 text-xs font-normal font-['Bricolage_Grotesque']">
                              {referent.relation_type}
                            </div>
                            <div className="w-2.5 h-0 origin-top-left rotate-90 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>
                            <div className="justify-start text-gray-800 text-xs font-normal font-['Bricolage_Grotesque']">
                              {referent.reminders_sent_count} Relance(s)
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="self-stretch inline-flex justify-end items-center gap-3">
                <button
                  onClick={() => onOpenChange(false)}
                  disabled={isLoading}
                  className="h-10 p-3 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-start items-center gap-1 overflow-hidden hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <div className="justify-center text-neutral-400 text-sm font-semibold font-['Bricolage_Grotesque']">
                    Annuler
                  </div>
                </button>
                <button
                  onClick={handleSendReminders}
                  disabled={isLoading || selectedReferents.length === 0}
                  className="h-10 p-3 bg-teal-400 rounded-[10px] flex justify-start items-center gap-1 overflow-hidden hover:bg-teal-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="justify-center text-white text-sm font-semibold font-['Bricolage_Grotesque']">
                    {isLoading ? 'Envoi en cours...' : 'Relancer'}
                  </div>
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
